import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Loader2, CheckCircle2, ArrowLeft } from "lucide-react";
import { toast, Toaster } from "react-hot-toast";
import apiUrls from "../../config/api-urls";
import tokenService from "../../services/token-service";
import SearchingHandymen from "./searching-handymen";

const DEFAULT_LAT = 31.5204;
const DEFAULT_LNG = 74.3587;
const DEFAULT_ADDRESS = "Model Town, Lahore";

export default function CustomerServiceRequest() {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [description, setDescription] = useState("");
  const [image, setImage] = useState(null);
  const [video, setVideo] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [address, setAddress] = useState(DEFAULT_ADDRESS);
  const [latitude, setLatitude] = useState(DEFAULT_LAT);
  const [longitude, setLongitude] = useState(DEFAULT_LNG);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState("idle");
  const [taskData, setTaskData] = useState(null);
  const [serviceType, setServiceType] = useState("live");
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("");
  const [isHydrated, setIsHydrated] = useState(false);

  const token = tokenService.getToken();

  useEffect(() => {
    const storedTask = localStorage.getItem("activeTask");
    if (storedTask) {
      setTaskData(JSON.parse(storedTask));
    }
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    const type = localStorage.getItem("serviceType") || "live";
    setServiceType(type);
    const today = new Date().toISOString().split("T")[0];
    setScheduleDate(today);
  }, []);

  useEffect(() => {
    const stored = localStorage.getItem("selectedCategory");
    if (stored) {
      setSelectedCategory(JSON.parse(stored));
    }

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const { latitude, longitude } = pos.coords;
          setLatitude(latitude);
          setLongitude(longitude);
          fetchAddressFromCoordinates(latitude, longitude);
        },
        (err) => console.error("Geolocation Error:", err),
      );
    }
  }, []);

  const fetchAddressFromCoordinates = async (lat, lng) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`,
      );
      const data = await response.json();
      if (data?.display_name) setAddress(data.display_name);
    } catch {
      setAddress(`Near ${lat.toFixed(4)}, ${lng.toFixed(4)}`);
    }
  };

  const updateCoordinatesFromAddress = async (address) => {
    if (!address || address.length < 3) return;

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}`,
      );
      const results = await response.json();

      if (results.length > 0) {
        const { lat, lon, display_name } = results[0];
        setLatitude(parseFloat(lat));
        setLongitude(parseFloat(lon));
        setAddress(display_name);
      } else {
        console.warn("No location found for this address");
      }
    } catch (error) {
      console.error("Address lookup error:", error);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setImage(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleVideoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setVideo(file);
      const reader = new FileReader();
      reader.onloadend = () => setVideoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCategory) return;

    if (!token) {
      toast.error("Please sign in or create an account to continue.");
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus("idle");

    try {
      const formData = new FormData();
      formData.append(
        "mainServiceId",
        selectedCategory.cartItems[0].mainService.id,
      );
      formData.append(
        "subServiceIds",
        JSON.stringify(selectedCategory.cartItems.map((item) => item.id)),
      );
      formData.append("description", description);
      formData.append(
        "location",
        JSON.stringify({
          type: "Point",
          coordinates: [longitude, latitude],
          address,
        }),
      );
      if (totalAmount) formData.append("basePrice", totalAmount);
      if (image) formData.append("image", image);
      if (video) formData.append("video", video);
      if (serviceType === "schedule") {
        formData.append("serviceType", "schedule");
        formData.append("scheduleDate", scheduleDate);
        formData.append("scheduleTime", scheduleTime);
      } else {
        formData.append("serviceType", "live");
      }
      const res = await fetch(`${apiUrls.services.customerServiceRequest}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        const backendMessage =
          (typeof data?.message === "string" && data.message) ||
          (typeof data?.error === "string" && data.error) ||
          (typeof data?.error?.message === "string" && data.error.message) ||
          "";

        const backendMessageLower =
          typeof backendMessage === "string"
            ? backendMessage.toLowerCase()
            : "";
        if (
          res.status === 401 ||
          res.status === 403 ||
          backendMessageLower.includes("failed to authenticate token")
        ) {
          toast.error("Please sign in or create an account to continue.");
          return;
        }
        throw new Error(backendMessage || "Failed to create service request");
      }
      const response = data.data;

      setTaskData(response);
      localStorage.setItem("activeTask", JSON.stringify(response));
      setSubmitStatus("success");

      setDescription("");
      setImage(null);
      setVideo(null);
      setImagePreview(null);
      setVideoPreview(null);
      localStorage.removeItem("cartItems");
      setSelectedCategory(null);
      if (response.serviceType === "schedule") {
        navigate("/customer/schedule");
      }
    } catch (err) {
      toast.error(err.message || "Failed to create request");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    window.history.back();
  };

  const totalAmount =
    selectedCategory?.cartItems?.reduce(
      (sum, item) => sum + (item.customPrice || 0),
      0,
    ) || 0;
  if (!isHydrated) return null;
  if (taskData) {
    return (
      <SearchingHandymen
        task={taskData}
        onOfferSelected={(data) => {
          navigate("/customer/service-request/live", {
            state: {
              task: data?.task,
              handyman: data?.handyman,
              serviceRequestId: data?.serviceRequestId,
            },
          });
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4">
      <Toaster position="top-right" reverseOrder={false} />
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 font-medium rounded-lg transition-colors"
              aria-label="Go back"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <h1 className="text-3xl font-bold text-slate-900">
              Create Service Request
            </h1>
          </div>
        </div>

        {submitStatus === "success" && serviceType === "live" && (
          <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-lg flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            <p className="font-medium text-emerald-900">
              Service request created successfully!
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {selectedCategory && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <h2 className="text-lg font-semibold mb-4">Selected Services</h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {selectedCategory.cartItems.map((item) => (
                  <div
                    key={item.id}
                    className="bg-slate-50 rounded-lg border border-slate-200 p-3 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 border">
                          <img
                            src={item.image || "/placeholder.svg"}
                            alt={item.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-slate-800 text-sm truncate">
                          {item.name}
                        </h3>
                        <p className="text-blue-600 font-bold text-sm mt-1">
                          Rs. {item.customPrice || 0}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 flex justify-between border-t pt-4 text-lg font-semibold">
                <span>Total Amount:</span>
                <span className="text-blue-600">Rs. {totalAmount}</span>
              </div>
            </div>
          )}

          {serviceType === "schedule" && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <label className="block text-sm font-semibold text-slate-900 mb-3">
                Choose Date
              </label>
              <input
                type="date"
                value={scheduleDate}
                onChange={(e) => setScheduleDate(e.target.value)}
                min={new Date().toISOString().split("T")[0]}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />

              <label className="block text-sm font-semibold text-slate-900 mb-3 mt-4">
                Choose Time
              </label>
              <input
                type="time"
                value={scheduleTime}
                onChange={(e) => setScheduleTime(e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
          )}

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <label className="block text-sm font-semibold text-slate-900 mb-3">
              Description of Your Need
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe what you need help with..."
              rows={4}
              className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition resize-none"
            />
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <label className="block text-sm font-semibold text-slate-900 mb-3">
              Upload Image (Optional)
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg"
            />
            {imagePreview && (
              <img
                src={imagePreview}
                alt="Preview"
                className="mt-3 w-32 h-32 object-cover rounded-lg"
              />
            )}
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <label className="block text-sm font-semibold text-slate-900 mb-3">
              Upload Video (Optional)
            </label>
            <input
              type="file"
              accept="video/*"
              onChange={handleVideoChange}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg"
            />
            {videoPreview && (
              <video
                src={videoPreview}
                controls
                className="mt-3 w-32 h-32 rounded-lg object-cover"
              />
            )}
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <label className="flex items-center gap-2 text-sm font-semibold mb-4 text-slate-900">
              <MapPin className="w-4 h-4" /> Location Details
            </label>
            <input
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              onBlur={() => updateCoordinatesFromAddress(address)}
              className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="flex gap-4">
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 py-3 px-6 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                "Create Service Request"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
